import { Module } from "vuex";
import Notify from 'quasar/src/plugins/Notify.js';;
import i18n from "@/i18n";
import { logger } from "@/utils/logger";
import store from "@/store";
import { ImportExportParamState } from "@/types";
import {
  createImport,
  deleteImport as deleteImportApi,
  exportParam,
  getEvents,
  getImports,
  importParam,
} from "@/store/services/configuration/importExportParamService";

function setGlobalLoading(value: boolean) {
  store.state.isLoading = value;
}

const importExportParamModule: Module<ImportExportParamState, unknown> = {
  namespaced: true,
  state: {
    isExporting: false,
    isCreating: false,
    isActivating: false,
    isLoadingFiles: false,
    isLoadingHistory: false,
    fileRows: [],
    historyRows: [],
  },
  getters: {
    isExporting: (state) => state.isExporting,
    isCreating: (state) => state.isCreating,
    isActivating: (state) => state.isActivating,
    isLoadingFiles: (state) => state.isLoadingFiles,
    isLoadingHistory: (state) => state.isLoadingHistory,
    fileRows: (state) => state.fileRows,
    historyRows: (state) => state.historyRows,
  },
  mutations: {
    setExporting(state, payload: boolean) {
      state.isExporting = payload;
    },
    setCreating(state, payload: boolean) {
      state.isCreating = payload;
    },
    setActivating(state, payload: boolean) {
      state.isActivating = payload;
    },
    setLoadingFiles(state, payload: boolean) {
      state.isLoadingFiles = payload;
    },
    setLoadingHistory(state, payload: boolean) {
      state.isLoadingHistory = payload;
    },
    setFileRows(state, payload) {
      state.fileRows = payload;
    },
    setHistoryRows(state, payload) {
      state.historyRows = payload;
    },
  },
  actions: {
    async exportParam({ commit }) {
      commit("setExporting", true);
      setGlobalLoading(true);
      try {
        await exportParam();
        Notify.create({
          type: "positive",
          message: i18n.global.t("importExportParam.exportSuccess"),
          timeout: 4000,
        });
      } catch (error) {
        logger.error(error);
        Notify.create({
          type: "negative",
          message: i18n.global.t("importExportParam.exportError"),
          timeout: 5000,
        });
        throw error;
      } finally {
        commit("setExporting", false);
        setGlobalLoading(false);
      }
    },
    async fetchImports({ commit }) {
      commit("setLoadingFiles", true);
      try {
        commit("setFileRows", await getImports());
      } catch (error) {
        logger.error(error);
        commit("setFileRows", []);
        Notify.create({
          type: "negative",
          message: i18n.global.t("importExportParam.loadError"),
          timeout: 5000,
        });
        throw error;
      } finally {
        commit("setLoadingFiles", false);
      }
    },
    async fetchEvents({ commit }) {
      commit("setLoadingHistory", true);
      try {
        commit("setHistoryRows", await getEvents());
      } catch (error) {
        logger.error(error);
        commit("setHistoryRows", []);
        Notify.create({
          type: "negative",
          message: i18n.global.t("importExportParam.loadError"),
          timeout: 5000,
        });
        throw error;
      } finally {
        commit("setLoadingHistory", false);
      }
    },
    async createImport({ commit, dispatch }, payload) {
      commit("setCreating", true);
      setGlobalLoading(true);
      try {
        await createImport(payload);
        Notify.create({
          type: "positive",
          message: i18n.global.t("importExportParam.addSuccess"),
          timeout: 4000,
        });
        try {
          await dispatch("fetchImports");
        } catch {
          // already notified by fetchImports
        }
      } catch (error) {
        logger.error(error);
        Notify.create({
          type: "negative",
          message: i18n.global.t("importExportParam.addError"),
          timeout: 5000,
        });
        throw error;
      } finally {
        commit("setCreating", false);
        setGlobalLoading(false);
      }
    },
    async activateImport({ commit, dispatch }, resourceUid: string) {
      commit("setActivating", true);
      setGlobalLoading(true);
      try {
        await importParam(resourceUid);
        Notify.create({
          type: "positive",
          message: i18n.global.t("importExportParam.activateSuccess"),
          timeout: 4000,
        });
        try {
          await dispatch("fetchImports");
        } catch {
          // already notified by fetchImports
        }
      } catch (error) {
        logger.error(error);
        Notify.create({
          type: "negative",
          message: i18n.global.t("importExportParam.activateError"),
          timeout: 5000,
        });
        throw error;
      } finally {
        try {
          await dispatch("fetchEvents");
        } catch {
          // already notified by fetchEvents
        }
        commit("setActivating", false);
        setGlobalLoading(false);
      }
    },
    async deleteImport({ commit, dispatch }, resourceUid: string) {
      try {
        await deleteImportApi(resourceUid);
        Notify.create({
          type: "positive",
          message: i18n.global.t("importExportParam.deleteSuccess"),
          timeout: 4000,
        });
        try {
          await dispatch("fetchImports");
        } catch {
          // already notified by fetchImports
        }
      } catch (error) {
        logger.error(error);
        Notify.create({
          type: "negative",
          message: i18n.global.t("importExportParam.deleteError"),
          timeout: 5000,
        });
      }
    },
  },
};

export default importExportParamModule;
